interface RejectionCapPanelProps {
  /** Which review step the user is capped on. */
  step: 'copy' | 'visual';
  /** Step 1: back to the two captions. Step 2: approve what is rendered as it stands. */
  onAcceptBest: () => void;
  /** The second path, Review Step 2 only — anything we rendered can be layer-edited. */
  onEditLayers?: () => void;
  isBusy?: boolean;
}

/**
 * The rejection cap (SRS invariant): a request lineage allows three
 * regeneration attempts. Once they are spent, this panel replaces the
 * regenerate action for good.
 *
 * It offers exactly the two paths the SRS names and nothing else — no retry, no
 * fourth attempt, no "are you sure". At Review Step 1 both paths are the same
 * thing (choose one of the two captions on the table), so the panel says so
 * plainly rather than inventing a second button to look balanced.
 *
 * It is driven by the request row (`attempt`), not by a 409 this session
 * happened to catch, so a reviewer who reloads a capped lineage lands back on
 * this panel instead of on a button the server will refuse. The refusal is
 * still honoured as a fallback — the server is the enforcer.
 *
 * What the cap closes is **regeneration**. A revision and a redesign spend a
 * different budget and are not attempts, so they stay available; the panel
 * names the two ways to finish, not the only two things left on the screen.
 */
export function RejectionCapPanel({
  step,
  onAcceptBest,
  onEditLayers,
  isBusy = false,
}: RejectionCapPanelProps) {
  return (
    <section className="cap-panel">
      <h3 className="cap-panel__title">Three tries, all used</h3>
      <p className="cap-panel__lede">
        This post has been written from scratch three times. That is the limit — a fourth pass uses
        a post and, past three, almost never produces something better. Two ways forward from here.
      </p>

      <div className="cap-panel__paths">
        <button
          className="button button--primary"
          type="button"
          disabled={isBusy}
          onClick={onAcceptBest}
        >
          {step === 'copy' ? 'Accept current best' : 'Approve as it stands'}
        </button>

        {step === 'visual' && onEditLayers ? (
          <button className="button" type="button" disabled={isBusy} onClick={onEditLayers}>
            Edit layers
          </button>
        ) : null}
      </div>

      <p className="cap-panel__note">
        {step === 'copy'
          ? 'Accepting takes you back to the two captions below — pick the stronger one and it goes out. Asking for a revision is still open: it rewrites one caption and uses no try.'
          : 'Layer edits change the text, the background and the logo placement. They redraw the picture; they never rewrite anything. Asking for a revision or a redesign is still open too — neither uses a try.'}
      </p>
    </section>
  );
}
